import { create } from "zustand";
import {
  connectEvidenceSocket,
  disconnectEvidenceSocket,
  subscribeToEvidence,
  unsubscribeFromEvidence,
  getEvidenceSocket,
  hasAnyFilter,
} from "../../evidenceSocket";
import type { EvidenceEvent, EvidenceFilter } from "../../evidenceSocket";

export interface EvidenceItem extends EvidenceEvent {
  _localId: string;
  _receivedAt: number;
}

export type EvidenceStatusFilter = "all" | "ok" | "error";

interface EvidenceSocketStore {
  evidences: EvidenceItem[];
  isConnected: boolean;
  connecting: boolean;
  error: string | null;
  currentFilter: EvidenceFilter | null;
  joinedRooms: string[];
  maxBuffer: number;

  selectedAdapter: string | null;
  selectedRetransmission: string | null;
  statusFilter: EvidenceStatusFilter;
  searchQuery: string;
  debouncedSearchQuery: string;

  connect: (filter: EvidenceFilter) => void;
  disconnect: () => void;
  updateSubscription: (filter: EvidenceFilter) => void;
  addEvidence: (ev: EvidenceEvent) => void;
  clearEvidences: () => void;

  setSelectedAdapter: (adapter: string | null) => void;
  setSelectedRetransmission: (retransmissionId: string | null) => void;
  setStatusFilter: (status: EvidenceStatusFilter) => void;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;

  setConnectionStatus: (status: boolean) => void;
  setJoinedRooms: (rooms: string[]) => void;
  resetStore: () => void;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let localIdCounter = 0;

let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let intentionalClose = false;

const DEFAULT_MAX_BUFFER = 100;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 5000;

const nextLocalId = (): string => {
  localIdCounter += 1;
  return `${Date.now()}-${localIdCounter}`;
};

export const useEvidenceSocketStore = create<EvidenceSocketStore>(
  (set, get) => ({
    evidences: [],
    isConnected: false,
    connecting: false,
    error: null,
    currentFilter: null,
    joinedRooms: [],
    maxBuffer: DEFAULT_MAX_BUFFER,

    selectedAdapter: null,
    selectedRetransmission: null,
    statusFilter: "all",
    searchQuery: "",
    debouncedSearchQuery: "",

    connect: (filter: EvidenceFilter) => {
      intentionalClose = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      set({ currentFilter: filter, connecting: true });

      try {
        const socket = connectEvidenceSocket();

        socket.removeAllListeners();

        const subscribe = () => {
          const activeFilter = get().currentFilter;
          if (activeFilter && hasAnyFilter(activeFilter)) {
            subscribeToEvidence(activeFilter);
          }
        };

        const scheduleReconnect = () => {
          if (intentionalClose || reconnectTimer) {
            return;
          }
          if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            set({
              connecting: false,
              isConnected: false,
              error: "No se pudo reconectar",
            });
            return;
          }
          const delay = Math.min(
            RECONNECT_BASE_DELAY * 2 ** reconnectAttempts,
            RECONNECT_MAX_DELAY
          );
          set({ connecting: true });
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectAttempts += 1;
            disconnectEvidenceSocket();
            getEvidenceSocket().removeAllListeners();
            get().connect(get().currentFilter ?? filter);
          }, delay);
        };

        if (socket.connected) {
          reconnectAttempts = 0;
          set({ isConnected: true, connecting: false, error: null });
          subscribe();
        }

        socket.on("connect", () => {
          reconnectAttempts = 0;
          set({ isConnected: true, connecting: false, error: null });
          subscribe();
        });

        socket.on("disconnect", () => {
          set({ isConnected: false });
          if (intentionalClose) return;
          scheduleReconnect();
        });

        socket.on("connect_error", (err) => {
          set({ error: err.message, isConnected: false });
          if (intentionalClose) return;
          scheduleReconnect();
        });

        socket.on("subscribed:evidence", (response) => {
          set({ joinedRooms: response?.rooms ?? [] });
        });

        socket.on("evidence", (ev) => {
          get().addEvidence(ev);
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : "Error de conexión";
        set({ error: message });
      }
    },

    disconnect: () => {
      intentionalClose = true;
      reconnectAttempts = 0;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      const { currentFilter } = get();

      if (currentFilter && hasAnyFilter(currentFilter)) {
        unsubscribeFromEvidence(currentFilter);
      }

      const socket = getEvidenceSocket();
      socket.removeAllListeners();

      disconnectEvidenceSocket();
      set({ isConnected: false, connecting: false, joinedRooms: [] });
    },

    updateSubscription: (filter: EvidenceFilter) => {
      const { currentFilter, isConnected } = get();

      if (isConnected && currentFilter && hasAnyFilter(currentFilter)) {
        unsubscribeFromEvidence(currentFilter);
      }

      set({ currentFilter: filter });

      if (isConnected && hasAnyFilter(filter)) {
        subscribeToEvidence(filter);
      }
    },

    addEvidence: (ev: EvidenceEvent) => {
      set((state) => {
        const item: EvidenceItem = {
          ...ev,
          _localId: nextLocalId(),
          _receivedAt: Date.now(),
        };

        const next = [item, ...state.evidences];
        if (next.length > state.maxBuffer) {
          next.length = state.maxBuffer;
        }
        return { evidences: next };
      });
    },

    clearEvidences: () => {
      set({ evidences: [] });
    },

    setSelectedAdapter: (adapter) => set({ selectedAdapter: adapter }),

    setSelectedRetransmission: (retransmissionId) =>
      set({ selectedRetransmission: retransmissionId }),

    setStatusFilter: (status) => set({ statusFilter: status }),

    setSearchQuery: (query) => {
      set({ searchQuery: query });

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        set({ debouncedSearchQuery: query });
      }, 300);
    },

    clearSearch: () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      set({ searchQuery: "", debouncedSearchQuery: "" });
    },

    setConnectionStatus: (status) => set({ isConnected: status }),

    setJoinedRooms: (rooms) => set({ joinedRooms: rooms }),

    resetStore: () => {
      set({
        evidences: [],
        isConnected: false,
        connecting: false,
        error: null,
        currentFilter: null,
        joinedRooms: [],
        selectedAdapter: null,
        selectedRetransmission: null,
        statusFilter: "all",
        searchQuery: "",
        debouncedSearchQuery: "",
      });
    },
  })
);
